const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

const LOGO_URL = `${SUPABASE_URL}/storage/v1/object/public/logo/AppIcon.png`;
const APP_URL = 'https://mymovement.space';
const WEB_APP_URL = 'https://app.mymovement.space';
const INSTAGRAM_HANDLE = 'mymovement.space';
const INSTAGRAM_URL = `https://www.instagram.com/${INSTAGRAM_HANDLE}`;
const APP_STORE_URL = 'https://apple.co/4uabaEs';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=space.mymovement.app';
const UNSUBSCRIBE_URL = '{{{unsubscribe_url}}}';

// ==========================================================================
// Curated athlete photo pool — IG feed picks 3 random photos from this list
// every time an email is rendered/sent. Each send gets a fresh random subset.
// ==========================================================================
const IG_FEED_POOL = [
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/Mieke Wall Squat.JPG' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/Mieke Resistance Bands.JPG' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/Mieke Mueller Y Balance 1.JPG' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/Mieke Mueller Y Balance 2.JPG' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/Mieke Mueller Y Balance 3.JPG' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/Mieke Muller 1.jpeg' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/Mieke Muller 2.JPG' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC02515.jpeg' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC02534.jpeg' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC02552.jpeg' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC02580.jpeg' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC02607.jpeg' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC02632.jpeg' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC02651.jpeg' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC02993.JPG' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC03272.JPG' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC03943.JPG' },
  { bucket: 'shoulder-program', path: 'Minke Mueller 2/DSC03950.JPG' },
];

const IG_PARAMS = 'width=360&height=640&resize=cover&quality=80';
const IG_THUMB_W = 184;
const IG_THUMB_H = 327;

function buildFeedUrl({ bucket, path }) {
  const encodedPath = path.split('/').map(encodeURIComponent).join('/');
  return `${SUPABASE_URL}/storage/v1/render/image/public/${bucket}/${encodedPath}?${IG_PARAMS}`;
}

function pickRandomFeed(count = 3) {
  const pool = [...IG_FEED_POOL];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count).map(buildFeedUrl);
}

// ==========================================================================
// Vimeo helpers
// ==========================================================================
export function extractVimeoId(url) {
  if (!url) return null;
  const match = String(url).match(/(?:vimeo\.com\/(?:video\/|showcase\/[^/]+\/video\/|channels\/[^/]+\/)?|player\.vimeo\.com\/video\/)(\d+)/);
  return match ? match[1] : null;
}

function buildVimeoThumb(videoId) {
  return `https://vumbnail.com/${videoId}.jpg`;
}

const PLAY_ICON = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 72 72"><circle cx="36" cy="36" r="36" fill="rgba(255,255,255,0.9)"/><polygon points="29,22 29,50 52,36" fill="#000000"/></svg>'
);

// ==========================================================================
// i18n — template string translations
// ==========================================================================
const I18N = {
  en: {
    greeting: (name) => `Dear ${name}.`,
    cta: 'Open MyMovement.Space',
    appStore: 'Get the iOS app',
    appStoreSubtitle: 'Movement &middot; Mobility &middot; Coaching',
    appStoreLink: 'View on the App Store',
    googlePlay: 'Get the Android app',
    googlePlayLink: 'Get it on Google Play',
    igTitle: 'Follow our journey',
    igSubtitle: 'Meet the athletes &amp; community',
    igCta: (handle) => `Follow @${handle}`,
    igCaption: 'Daily mobility tips, athlete stories &amp; behind-the-scenes',
    videosLabel: 'Videos',
    videosHeadline: 'watch & learn',
    videoLink: 'Watch on Vimeo',
    footerTagline: 'Your Life Companion for Health and Fitness',
    footerExplainer: "You received this email because you're a MyMovement.Space member.",
    coachingFooterExplainer: "You're receiving this email from your MyMovement.Space coaching team because you train with us. Don't want coaching emails?",
    visitWebsite: 'Visit website',
    unsubscribe: 'Unsubscribe',
    coachingUnsubscribe: 'Unsubscribe from coaching emails',
  },
  de: {
    greeting: (name) => `Hallo ${name}.`,
    cta: 'MyMovement.Space &ouml;ffnen',
    appStore: 'Hol Dir die iOS App',
    appStoreSubtitle: 'Bewegung &middot; Mobility &middot; Coaching',
    appStoreLink: 'Im App Store ansehen',
    googlePlay: 'Hol Dir die Android App',
    googlePlayLink: 'Bei Google Play ansehen',
    igTitle: 'Folge unserer Reise',
    igSubtitle: 'Lerne die Community kennen',
    igCta: (handle) => `Folge @${handle}`,
    igCaption: 'T&auml;gliche Mobility-Tipps, Athleten-Stories &amp; Behind-the-Scenes',
    videosLabel: 'Videos',
    videosHeadline: 'anschauen & lernen',
    videoLink: 'Auf Vimeo ansehen',
    footerTagline: 'Dein Begleiter f&uuml;r Gesundheit und Fitness',
    footerExplainer: 'Du erh&auml;ltst diese E-Mail, weil Du Mitglied bei MyMovement.Space bist.',
    coachingFooterExplainer: 'Du erh&auml;ltst diese E-Mail von deinem MyMovement.Space Coaching-Team, weil du mit uns trainierst. Keine Coaching-E-Mails mehr?',
    visitWebsite: 'Webseite besuchen',
    unsubscribe: 'Abmelden',
    coachingUnsubscribe: 'Von Coaching-E-Mails abmelden',
  },
};

function t(language, key, ...args) {
  const strings = I18N[language] || I18N.en;
  const val = strings[key] || I18N.en[key];
  return typeof val === 'function' ? val(...args) : val;
}

// ==========================================================================
// Brand tokens — dark mode, mirrors the iOS app
// ==========================================================================
const BG = '#000000';
const CARD_BG = '#141414';
const CARD_NESTED = '#1a1a1a';
const BRAND_GREEN = '#B8E600';
const TEXT_PRIMARY = '#FFFFFF';
const TEXT_BODY = 'rgba(255,255,255,0.85)';
const TEXT_SECONDARY = 'rgba(255,255,255,0.7)';
const TEXT_MUTED = 'rgba(255,255,255,0.5)';
const DIVIDER = 'rgba(255,255,255,0.08)';
const HEADER_DIVIDER = 'rgba(184,230,0,0.2)';

const FONT_HEADLINE = "'Syne','Helvetica Neue',Helvetica,Arial,sans-serif";
const FONT_BODY = "'Inter',-apple-system,'Helvetica Neue',Helvetica,Arial,sans-serif";
const GOOGLE_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Inter:wght@400;500;600;700&display=swap';
const EMAIL_SIGNATURE = 'MyMovement.Space Team';

// ==========================================================================
// Section renderers
// ==========================================================================

/**
 * Hero card — bold Syne headline (lowercase) on brand green, with optional
 * accent-word underline and hero image.
 */
function renderHero(hero) {
  if (!hero) return '';
  const { headline, accentWord, tagline, imageUrl } = hero;
  const safeImageUrl = imageUrl?.trim();

  // Image-only hero — full-width photo, no lime card or headline (activation template).
  if (safeImageUrl && (!headline || !headline.trim())) {
    return `
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:20px;">
    <tr>
      <td align="center">
        <img src="${safeImageUrl}" alt="" style="display:block;width:100%;max-width:100%;height:auto;border-radius:14px;"/>
      </td>
    </tr>
  </table>`;
  }

  if (!headline?.trim()) return '';

  let renderedHeadline = headline;
  if (accentWord && accentWord.trim()) {
    const safe = accentWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(${safe})`, 'i');
    renderedHeadline = headline.replace(
      re,
      `<span style="display:inline-block;border-bottom:4px solid ${BG};padding-bottom:2px;">$1</span>`
    );
  }

  return `
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:20px;">
    <tr>
      <td style="background-color:${BRAND_GREEN};border-radius:20px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="padding:28px 24px ${imageUrl ? '0' : '28px'} 24px;">
              <div style="font-family:${FONT_HEADLINE};font-weight:800;font-size:32px;line-height:1.08;letter-spacing:-0.03em;color:${BG};text-transform:lowercase;">
                ${renderedHeadline}
              </div>
              ${tagline ? `
              <div style="margin-top:12px;font-family:${FONT_BODY};font-size:14px;line-height:1.45;color:${BG};opacity:0.65;font-weight:500;">
                ${tagline}
              </div>` : ''}
            </td>
          </tr>
          ${imageUrl ? `
          <tr>
            <td align="center" style="padding:20px 24px 0 24px;">
              <img src="${imageUrl}" alt="" style="display:block;max-width:100%;height:auto;border-radius:14px;"/>
            </td>
          </tr>
          <tr><td style="height:20px;line-height:20px;font-size:0;">&nbsp;</td></tr>
          ` : ''}
        </table>
      </td>
    </tr>
  </table>`;
}

/**
 * Video section — dark card with Vimeo thumbnails + play overlay.
 */
function renderVideos(videos, language = 'en') {
  if (!videos || videos.length === 0) return '';
  const validVideos = videos
    .map((url) => ({ url, id: extractVimeoId(url) }))
    .filter((v) => v.id);

  if (validVideos.length === 0) return '';

  return `
  <tr>
    <td style="padding-top:16px;">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${CARD_BG};border-radius:20px;">
        <tr>
          <td style="padding:32px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td style="padding-bottom:4px;">
                  <div style="font-family:${FONT_BODY};font-size:11px;color:${TEXT_MUTED};text-transform:uppercase;letter-spacing:0.14em;font-weight:600;">${t(language, 'videosLabel')}</div>
                </td>
              </tr>
              <tr>
                <td style="padding-bottom:20px;">
                  <div style="font-family:${FONT_HEADLINE};font-size:20px;font-weight:700;color:${TEXT_PRIMARY};letter-spacing:-0.02em;text-transform:lowercase;">${t(language, 'videosHeadline')}</div>
                </td>
              </tr>
            </table>

            ${validVideos.map((v) => `
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:16px;">
              <tr>
                <td>
                  <a href="${v.url}" style="text-decoration:none;color:inherit;display:block;">
                    <!--[if gte mso 9]>
                    <v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:504px;height:283px;border-radius:14px;">
                      <v:fill type="frame" src="${buildVimeoThumb(v.id)}" color="#1a1a1a"/>
                      <v:textbox inset="0,0,0,0">
                    <![endif]-->
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${CARD_NESTED} url('${buildVimeoThumb(v.id)}') center center / cover no-repeat;border-radius:14px;">
                      <tr>
                        <td height="260" align="center" valign="middle" style="padding:70px 0;">
                          <img src="${PLAY_ICON}" alt="Play" width="72" height="72" style="display:block;border:0;"/>
                        </td>
                      </tr>
                    </table>
                    <!--[if gte mso 9]></v:textbox></v:rect><![endif]-->
                  </a>
                </td>
              </tr>
              <tr>
                <td style="padding-top:10px;">
                  <a href="${v.url}" style="font-family:${FONT_BODY};font-size:14px;color:${TEXT_SECONDARY};text-decoration:none;font-weight:600;">
                    <span style="color:${BRAND_GREEN};font-weight:500;">&#9658;</span>
                    &nbsp;${t(language, 'videoLink')}
                  </a>
                </td>
              </tr>
            </table>
            `).join('')}

          </td>
        </tr>
      </table>
    </td>
  </tr>`;
}

function renderStoreCard(lang, { label, url, linkLabel }) {
  return `
                    <div style="font-family:${FONT_BODY};font-size:13px;color:${TEXT_MUTED};text-transform:uppercase;letter-spacing:0.5px;font-weight:500;margin-bottom:16px;">${label}</div>
                    <a href="${url}" style="display:block;text-decoration:none;color:inherit;max-width:360px;margin:0 auto;">
                      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${CARD_NESTED};border-radius:16px;border:1px solid ${DIVIDER};">
                        <tr>
                          <td style="padding:16px;">
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                              <tr>
                                <td width="72" style="vertical-align:middle;padding-right:14px;">
                                  <img src="${LOGO_URL}" alt="MyMovement.Space" width="64" height="64" style="display:block;border-radius:14px;"/>
                                </td>
                                <td style="vertical-align:middle;text-align:left;">
                                  <div style="font-family:${FONT_BODY};font-size:17px;font-weight:700;color:${TEXT_PRIMARY};letter-spacing:-0.01em;">MyMovement.Space</div>
                                  <div style="font-family:${FONT_BODY};font-size:13px;color:${TEXT_MUTED};margin-top:4px;line-height:1.35;">${t(lang, 'appStoreSubtitle')}</div>
                                  <div style="font-family:${FONT_BODY};font-size:13px;color:${BRAND_GREEN};margin-top:10px;font-weight:600;">${linkLabel}</div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </table>
                    </a>`;
}

function renderAppStoreCard(lang) {
  return `
          <tr><td style="height:20px;line-height:20px;font-size:0;">&nbsp;</td></tr>
          <tr>
            <td>
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${CARD_BG};border-radius:20px;">
                <tr>
                  <td align="center" style="padding:32px;">
                    ${renderStoreCard(lang, { label: t(lang, 'appStore'), url: APP_STORE_URL, linkLabel: t(lang, 'appStoreLink') })}
                    <div style="height:28px;line-height:28px;font-size:0;">&nbsp;</div>
                    ${renderStoreCard(lang, { label: t(lang, 'googlePlay'), url: GOOGLE_PLAY_URL, linkLabel: t(lang, 'googlePlayLink') })}
                  </td>
                </tr>
              </table>
            </td>
          </tr>`;
}

// ==========================================================================
// Main wrapper
// ==========================================================================

// Keep in step with supabase/functions/email-send/content.ts so the preview matches the send.
const GREETING_WORDS = new Set([
  'hallo', 'hello', 'hi', 'hey', 'hej', 'dear', 'liebe', 'lieber', 'liebes',
  'guten', 'moin', 'servus', 'grüß', 'gruss',
]);

function stripDocumentWrapper(html) {
  if (!/<(?:!doctype|html|head|body)\b/i.test(html)) return html;
  return html
    .replace(/<!doctype[^>]*>/gi, '')
    .replace(/<head\b[\s\S]*?<\/head>/gi, '')
    .replace(/<\/?(?:html|body)\b[^>]*>/gi, '')
    .trim();
}

function startsWithGreeting(html) {
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  const first = text.match(/^\p{L}+/u)?.[0];
  return first !== undefined && GREETING_WORDS.has(first);
}

/**
 * Wraps email body HTML in the branded MyMovement.Space dark template.
 * Mirrors the iOS app: black canvas, #141414 cards, Syne headlines, Inter body.
 */
export function wrapInTemplate(rawBodyHtml, options = {}) {
  const bodyHtml = stripDocumentWrapper(rawBodyHtml || '');
  const {
    preheader = '',
    showFooter = true,
    showCta = true,
    showInstagramFeed = true,
    hero = null,
    videos = [],
    recipientName = '',
    language = 'en',
    messageClass,
  } = options;

  const lang = I18N[language] ? language : 'en';
  const isCoaching = messageClass === 'coaching';
  const [igPost1, igPost2, igPost3] = pickRandomFeed(3);

  return `<!DOCTYPE html>
<html lang="${lang}" xmlns="http://www.w3.org/1999/xhtml">
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <meta http-equiv="X-UA-Compatible" content="IE=edge"/>
  <meta name="color-scheme" content="dark"/>
  <meta name="supported-color-schemes" content="dark"/>
  <title>MyMovement.Space</title>
  <!--[if !mso]><!-->
  <link href="${GOOGLE_FONTS_URL}" rel="stylesheet"/>
  <!--<![endif]-->
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
  <style>
    @import url('${GOOGLE_FONTS_URL}');

    :root { color-scheme: dark; supported-color-schemes: dark; }

    .email-body img {
      max-width: 100% !important;
      height: auto !important;
      display: block;
      margin: 0 auto;
    }
    .email-body figure {
      margin: 20px 0 !important;
      text-align: center;
    }
    .email-body a {
      color: ${BRAND_GREEN} !important;
      text-decoration: underline;
    }
    .email-body h1,
    .email-body h2,
    .email-body h3 {
      font-family: ${FONT_HEADLINE};
      font-weight: 800;
      text-transform: lowercase;
      color: ${TEXT_PRIMARY};
      letter-spacing: -0.02em;
    }
    .email-body h1 { font-size: 28px; line-height: 1.1; margin: 0 0 16px; }
    .email-body h2 { font-size: 22px; line-height: 1.15; margin: 24px 0 12px; }
    .email-body h3 { font-size: 18px; line-height: 1.2; margin: 20px 0 10px; }
    .email-body p { margin: 0 0 16px; color: rgba(255,255,255,0.85); }
    .email-body strong { font-weight: 600; color: ${BRAND_GREEN}; }
    .email-body em { font-style: italic; font-size: 14px; color: rgba(255,255,255,0.6); }
    .email-body ul, .email-body ol { margin: 0 0 16px; padding: 0; list-style: none; counter-reset: li; }
    .email-body li {
      padding-left: 22px;
      margin-bottom: 12px;
      position: relative;
      line-height: 1.6;
      color: ${TEXT_PRIMARY};
    }
    .email-body ul > li::before {
      content: '\\25CF';
      color: ${BRAND_GREEN};
      font-size: 6px;
      position: absolute;
      left: 0;
      top: 7px;
    }
    .email-body ol > li::before {
      counter-increment: li;
      content: counter(li) '.';
      color: ${BRAND_GREEN};
      font-weight: 700;
      font-size: 16px;
      position: absolute;
      left: 0;
      top: 0;
    }

    @media only screen and (max-width: 480px) {
      .email-body figure { margin: 14px 0 !important; }
      .hero-headline { font-size: 26px !important; }
      .ig-grid td { padding: 5px !important; }
    }
  </style>
</head>
<body style="margin:0;padding:0;background-color:${BG};font-family:${FONT_BODY};-webkit-font-smoothing:antialiased;color:${TEXT_PRIMARY};">
  ${preheader ? `<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;">${preheader}${'&zwnj;&nbsp;'.repeat(30)}</div>` : ''}

  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${BG};">
    <tr>
      <td align="center" style="padding:0 16px;">

        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;">

          <!-- Header: logomark (58px) + wordmark (Inter Bold 24px) -->
          <tr>
            <td style="padding:24px 0;">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="vertical-align:middle;padding-right:14px;">
                    <img src="${LOGO_URL}" alt="MyMovement.Space" width="58" height="58" style="display:block;"/>
                  </td>
                  <td style="vertical-align:middle;">
                    <div style="font-family:${FONT_BODY};font-size:24px;font-weight:700;color:${TEXT_PRIMARY};letter-spacing:-0.02em;line-height:1;">MyMovement.Space</div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Header divider: 1px lime green at 20% opacity -->
          <tr>
            <td style="border-bottom:1px solid ${HEADER_DIVIDER};height:1px;line-height:1px;font-size:0;">&nbsp;</td>
          </tr>
          <tr><td style="height:20px;line-height:20px;font-size:0;">&nbsp;</td></tr>

          ${hero ? `
          <!-- Hero card -->
          <tr>
            <td>${renderHero(hero)}</td>
          </tr>
          ` : ''}

          <!-- Body card (with CTA inside) -->
          <tr>
            <td>
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${CARD_BG};border-radius:20px;">
                <tr>
                  <td class="email-body" style="padding:32px;font-family:${FONT_BODY};font-size:16px;line-height:1.6;color:${TEXT_BODY};letter-spacing:-0.005em;">
                    ${recipientName && !startsWithGreeting(bodyHtml) ? `<p style="font-family:${FONT_BODY};font-size:22px;font-weight:700;color:${TEXT_PRIMARY};margin:0 0 20px;">${t(lang, 'greeting', recipientName)}</p>` : ''}${bodyHtml}
                    <p style="margin:24px 0 0;color:${TEXT_MUTED};font-family:${FONT_BODY};font-size:14px;line-height:1.5">${EMAIL_SIGNATURE}</p>
                  </td>
                </tr>
                ${showCta ? `
                <tr>
                  <td align="center" style="padding:0 32px 32px;">
                    <table role="presentation" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="padding-top:8px;">
                          <a href="${WEB_APP_URL}" style="display:inline-block;padding:16px 32px;background-color:${BRAND_GREEN};color:${BG};font-family:${FONT_BODY};font-size:18px;font-weight:600;text-decoration:none;border-radius:999px;letter-spacing:-0.01em;">${t(lang, 'cta')}</a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                ` : ''}
              </table>
            </td>
          </tr>

          ${renderVideos(videos, lang)}

          ${showFooter && !isCoaching ? `
          ${renderAppStoreCard(lang)}
          ` : ''}

          ${showFooter && showInstagramFeed && !isCoaching ? `
          <tr><td style="height:20px;line-height:20px;font-size:0;">&nbsp;</td></tr>

          <!-- IG card -->
          <tr>
            <td>
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${CARD_BG};border-radius:20px;">
                <tr>
                  <td style="padding:32px;">

                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td align="center" style="padding-bottom:6px;">
                          <div style="font-family:${FONT_BODY};font-size:16px;font-weight:700;color:${TEXT_PRIMARY};">${t(lang, 'igTitle')}</div>
                        </td>
                      </tr>
                      <tr>
                        <td align="center" style="padding-bottom:20px;">
                          <div style="font-family:${FONT_BODY};font-size:14px;font-weight:400;color:${TEXT_SECONDARY};">${t(lang, 'igSubtitle')}</div>
                        </td>
                      </tr>
                    </table>

                    <!-- Profile row -->
                    <table role="presentation" cellpadding="0" cellspacing="0" align="center" style="margin:0 auto;">
                      <tr>
                        <td style="vertical-align:middle;padding-right:12px;">
                          <img src="${LOGO_URL}" alt="" width="40" height="40" style="display:block;border-radius:999px;"/>
                        </td>
                        <td style="vertical-align:middle;">
                          <div style="font-family:${FONT_BODY};font-size:14px;font-weight:700;line-height:1.2;letter-spacing:-0.01em;"><a href="${INSTAGRAM_URL}" style="color:${TEXT_PRIMARY};text-decoration:underline;text-underline-offset:3px;">@${INSTAGRAM_HANDLE}</a></div>
                          <div style="font-family:${FONT_BODY};font-size:11px;color:${TEXT_MUTED};margin-top:2px;line-height:1.2;">Movement &middot; Mobility &middot; Coaching</div>
                        </td>
                      </tr>
                    </table>

                    <!-- 3-up photo grid (9:16 portrait, randomized, 12px gaps, 12px radius) -->
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:20px;" class="ig-grid">
                      <tr>
                        ${[igPost1, igPost2, igPost3].map((src) => `
                        <td width="33.33%" style="padding:6px;vertical-align:top;">
                          <a href="${INSTAGRAM_URL}" style="display:block;text-decoration:none;color:inherit;">
                            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${CARD_NESTED};border-radius:12px;overflow:hidden;">
                              <tr>
                                <td style="padding:0;line-height:0;">
                                  <img src="${src}" alt="" width="${IG_THUMB_W}" height="${IG_THUMB_H}" style="display:block;width:100%;height:auto;border-radius:12px;background-color:${CARD_NESTED};"/>
                                </td>
                              </tr>
                            </table>
                          </a>
                        </td>
                        `).join('')}
                      </tr>
                    </table>

                    <!-- Follow CTA (lime green pill) -->
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:20px;">
                      <tr>
                        <td align="center">
                          <a href="${INSTAGRAM_URL}" style="display:inline-block;padding:14px 28px;background-color:${BRAND_GREEN};color:${BG};font-family:${FONT_BODY};font-size:15px;font-weight:600;text-decoration:none;border-radius:999px;letter-spacing:-0.01em;">
                            ${t(lang, 'igCta', INSTAGRAM_HANDLE)}
                          </a>
                        </td>
                      </tr>
                      <tr>
                        <td align="center" style="padding-top:10px;font-family:${FONT_BODY};font-size:13px;color:${TEXT_MUTED};">
                          ${t(lang, 'igCaption')}
                        </td>
                      </tr>
                    </table>

                  </td>
                </tr>
              </table>
            </td>
          </tr>
          ` : ''}

          ${showFooter ? `
          <!-- Footer -->
          <tr><td style="height:20px;line-height:20px;font-size:0;">&nbsp;</td></tr>
          <tr>
            <td style="border-top:1px solid ${DIVIDER};height:1px;line-height:1px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td align="center" style="padding:32px 24px;font-family:${FONT_BODY};">
              <div style="font-size:14px;color:rgba(255,255,255,0.6);margin-bottom:8px;">${t(lang, 'footerTagline')}</div>
              <div style="font-size:12px;color:rgba(255,255,255,0.4);margin-bottom:12px;">${t(lang, isCoaching ? 'coachingFooterExplainer' : 'footerExplainer')}</div>
              <div style="font-size:12px;line-height:1.8;">
                <a href="${APP_URL}" style="color:rgba(255,255,255,0.6);text-decoration:underline;">${t(lang, 'visitWebsite')}</a>
                &nbsp;&middot;&nbsp;
                <a href="${INSTAGRAM_URL}" style="color:rgba(255,255,255,0.6);text-decoration:underline;">Instagram</a>
                &nbsp;&middot;&nbsp;
                <a href="${UNSUBSCRIBE_URL}" style="color:rgba(255,255,255,0.6);text-decoration:underline;">${t(lang, isCoaching ? 'coachingUnsubscribe' : 'unsubscribe')}</a>
              </div>
            </td>
          </tr>
          ` : ''}

        </table>

      </td>
    </tr>
  </table>
</body>
</html>`;
}
